import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Send, 
  ArrowLeft, 
  MoreVertical, 
  Trash2, 
  User,
  Search,
  X,
  Circle,
  Clock,
  Check,
  CheckCheck
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useDarkMode } from '../context/DarkModeContext';
import api from '../services/api';
import socketService from '../services/socket';

const Chat = () => {
  const navigate = useNavigate();
  const { chatId: urlChatId } = useParams();
  const { user } = useUser();
  const { isDarkMode } = useDarkMode();
  
  // Debug logging for user object
  console.log('Chat component user debug:', {
    user: user,
    user_id: user?.id,
    user_id_type: typeof user?.id
  });
  
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());

  const [loadingChat, setLoadingChat] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    loadConversations();
    initializeSocket();
    
    return () => {
      if (selectedChat) {
        socketService.leaveChat(selectedChat._id);
        socketService.offNewMessage(selectedChat._id);
        socketService.offUserTyping(selectedChat._id);
      }
      socketService.disconnect();
    };
  }, []);

  // Cleanup effect to prevent cycling
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Handle clicking outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowChatMenu(false);
      }
    };

    if (showChatMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChatMenu]);

  useEffect(() => {
    if (urlChatId && conversations.length > 0) {
      const chat = conversations.find(c => c._id === urlChatId);
      if (chat && (!selectedChat || selectedChat._id !== urlChatId)) {
        selectChat(chat);
      } else if (!chat && (!selectedChat || selectedChat._id !== urlChatId)) {
        // If chat not found in conversations, try to load it directly
        loadChatById(urlChatId);
      }
    }
  }, [urlChatId, conversations, selectedChat]);

  const loadChatById = async (chatId) => {
    if (loadingChat) return; // Prevent multiple simultaneous loads
    
    setLoadingChat(true);
    try {
      // Get chat details and messages in parallel for faster loading
      const [chatResponse, messagesResponse] = await Promise.all([
        api.getChatById(chatId),
        api.getChatMessages(chatId)
      ]);
      
      if (chatResponse.data) {
        const chat = chatResponse.data;
        const messages = messagesResponse.data || [];
        
        // Create a conversation object that matches the expected format
        const conversation = {
          _id: chat._id,
          otherUser: chat.participants.find(p => p._id !== user.id),
          lastMessage: messages.length > 0 ? messages[messages.length - 1] : null,
          unreadCount: 0,
          lastMessageAt: chat.lastMessageAt,
          createdAt: chat.createdAt
        };
        
        // Set chat and messages immediately
        setSelectedChat(conversation);
        setMessages(messages);
        
        // Join chat room and set up listeners
        socketService.joinChat(chatId);
        socketService.offNewMessage(chatId);
        socketService.offUserTyping(chatId);
        
        // Listen for new messages
        socketService.onNewMessage(chatId, (message) => {
          console.log('📨 Received message in loadChatById:', message);
          setMessages(prev => {
            // Check if this message is from the current user (optimistic update replacement)
            const isFromCurrentUser = message.senderId === user.id;
            
            if (isFromCurrentUser) {
              // Look for optimistic message with same content from current user
              const optimisticMessageIndex = prev.findIndex(msg => 
                msg._id.startsWith('temp_') && 
                msg.senderId === user.id && 
                msg.content === message.content
              );
              
              if (optimisticMessageIndex !== -1) {
                // Replace optimistic message with real message
                const newMessages = [...prev];
                newMessages[optimisticMessageIndex] = { ...message, sender: prev[optimisticMessageIndex].sender };
                return newMessages;
              }
            }
            
            // Add new message (from other user or no matching optimistic message exists)
            return [...prev, message];
          });
          
          // Update conversation list with latest message
          setConversations(prev => 
            prev.map(conv => 
              conv._id === chatId 
                ? { 
                    ...conv, 
                    unreadCount: 0,
                    lastMessage: {
                      content: message.content,
                      timestamp: message.timestamp,
                      senderId: message.senderId
                    },
                    lastMessageAt: message.timestamp
                  }
                : conv
            )
          );
        });
        
        // Mark messages as read in background (don't block UI)
        api.markMessagesAsRead(chatId).then(() => {
          // Update conversation unread count locally
          setConversations(prev => 
            prev.map(conv => 
              conv._id === chatId 
                ? { ...conv, unreadCount: 0 }
                : conv
            )
          );
          
          // Trigger a custom event to notify the ChatNotification component
          window.dispatchEvent(new CustomEvent('messagesRead', { 
            detail: { chatId: chatId } 
          }));
        });
        
        // Listen for typing indicators
        socketService.onUserTyping(chatId, (data) => {
          if (data.userId !== user.id) {
            if (data.isTyping === false) {
              // User stopped typing
              setTypingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.userId);
                return newSet;
              });
            } else {
              // User started typing
              setTypingUsers(prev => new Set([...prev, data.userId]));
              
              // Auto-remove typing indicator after 3 seconds
              setTimeout(() => {
                setTypingUsers(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(data.userId);
                  return newSet;
                });
              }, 3000);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading chat by ID:', error);
    } finally {
      setLoadingChat(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeSocket = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔌 Connecting to socket...');
        await socketService.connect(token);
        console.log('✅ Socket connected successfully');
        
        // Global message listener for all conversations
        socketService.socket.on('new_message', (message) => {
          console.log('📨 Received new message in global listener:', message);
          // Update conversation list with new message
          setConversations(prev => 
            prev.map(conv => {
              // Find the conversation this message belongs to
              if (conv._id === message.chatId || conv.otherUser?._id === message.senderId) {
                return {
                  ...conv,
                  lastMessage: {
                    content: message.content,
                    timestamp: message.timestamp,
                    senderId: message.senderId
                  },
                  lastMessageAt: message.timestamp,
                  // Increment unread count if not in this chat
                  unreadCount: selectedChat?._id === conv._id ? 0 : (conv.unreadCount || 0) + 1
                };
              }
              return conv;
            })
          );
        });
        
        // Add connection status listeners
        socketService.socket.on('connect', () => {
          console.log('🔌 Socket connected in Chat component');
        });
        
        socketService.socket.on('disconnect', () => {
          console.log('🔌 Socket disconnected in Chat component');
        });
        
        socketService.socket.on('connect_error', (error) => {
          console.error('❌ Socket connection error in Chat component:', error);
        });
        
      }
    } catch (error) {
      console.error('Failed to connect socket:', error);
    }
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await api.getConversations();
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectChat = async (chat) => {
    // Don't re-select the same chat
    if (selectedChat && selectedChat._id === chat._id) {
      return;
    }
    
    if (loadingChat) return; // Prevent multiple simultaneous loads
    
    setLoadingChat(true);
    
    // Leave previous chat if any
    if (selectedChat) {
      socketService.leaveChat(selectedChat._id);
      socketService.offNewMessage(selectedChat._id);
      socketService.offUserTyping(selectedChat._id);
    }

    setSelectedChat(chat);
    setMessages([]);
    setNewMessage('');
    
    // Load messages
    try {
      const response = await api.getChatMessages(chat._id);
      setMessages(response.data || []);
      
      // Mark messages as read
      await api.markMessagesAsRead(chat._id);
      
      // Update conversation unread count locally
      setConversations(prev => 
        prev.map(conv => 
          conv._id === chat._id 
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
      
      // Trigger a custom event to notify the ChatNotification component
      window.dispatchEvent(new CustomEvent('messagesRead', { 
        detail: { chatId: chat._id } 
      }));
      
      // Join chat room
      socketService.joinChat(chat._id);
      
      // Remove any existing listeners first
      socketService.offNewMessage(chat._id);
      socketService.offUserTyping(chat._id);
      
      // Listen for new messages
      socketService.onNewMessage(chat._id, (message) => {
        setMessages(prev => {
          // Check if this message is from the current user (optimistic update replacement)
          const isFromCurrentUser = message.senderId === user.id;
          
          if (isFromCurrentUser) {
            // Look for optimistic message with same content from current user
            const optimisticMessageIndex = prev.findIndex(msg => 
              msg._id.startsWith('temp_') && 
              msg.senderId === user.id && 
              msg.content === message.content
            );
            
            if (optimisticMessageIndex !== -1) {
              // Replace optimistic message with real message
              const newMessages = [...prev];
              newMessages[optimisticMessageIndex] = { ...message, sender: prev[optimisticMessageIndex].sender };
              return newMessages;
            }
          }
          
          // Add new message (from other user or no matching optimistic message exists)
          return [...prev, message];
        });
        
        // Mark as read if we're in the chat
        api.markMessagesAsRead(chat._id);
        // Update conversation list with latest message
        setConversations(prev => 
          prev.map(conv => 
            conv._id === chat._id 
              ? { 
                  ...conv, 
                  unreadCount: 0,
                  lastMessage: {
                    content: message.content,
                    timestamp: message.timestamp,
                    senderId: message.senderId
                  },
                  lastMessageAt: message.timestamp
                }
              : conv
          )
        );
      });
      
      // Listen for typing indicators
      socketService.onUserTyping(chat._id, (data) => {
        if (data.userId !== user.id) {
          if (data.isTyping === false) {
            // User stopped typing
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.userId);
              return newSet;
            });
          } else {
            // User started typing
            setTypingUsers(prev => new Set([...prev, data.userId]));
            
            // Auto-remove typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.userId);
                return newSet;
              });
            }, 3000);
          }
        }
      });
      
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoadingChat(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    const content = newMessage.trim();
    
    // Create optimistic message (appears immediately)
    const optimisticMessage = {
      _id: `temp_${Date.now()}`,
      senderId: user.id,
      content: content,
      timestamp: new Date(),
      sender: {
        _id: user.id,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        profileImage: user.profileImage
      }
    };
    
    // Add message to chat immediately (optimistic update)
    setMessages(prev => [...prev, optimisticMessage]);
    
    // Clear input and show loading
    setNewMessage('');
    setMessageLoading(true);
    
    try {
      // Stop typing indicator
      if (selectedChat) {
        socketService.stopTyping(selectedChat._id);
      }
      
      // Send via socket for real-time
      const isSocketReady = socketService.isSocketConnected();
      if (selectedChat && isSocketReady) {
        console.log('🚀 Sending message via Socket.IO');
        socketService.sendMessage(selectedChat._id, content);
      } else {
        console.log('📡 Sending message via REST API');
        // Fallback to REST API if socket is not connected
        const response = await api.sendMessage(selectedChat._id, content);
        
        // Replace optimistic message with real message from server
        if (response.success && response.data) {
          setMessages(prev => 
            prev.map(msg => 
              msg._id === optimisticMessage._id 
                ? { ...response.data, sender: optimisticMessage.sender }
                : msg
            )
          );
        }
      }
      
      // Update conversations to show last message
      setConversations(prev => 
        prev.map(conv => 
          conv._id === selectedChat._id 
            ? { ...conv, lastMessage: { content, timestamp: new Date(), senderId: user.id } }
            : conv
        )
      );
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message if failed
      setMessages(prev => prev.filter(msg => msg._id !== optimisticMessage._id));
      // Re-add message to input if failed
      setNewMessage(content);
    } finally {
      setMessageLoading(false);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    if (!isTyping && selectedChat) {
      setIsTyping(true);
      socketService.startTyping(selectedChat._id);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (selectedChat) {
        socketService.stopTyping(selectedChat._id);
      }
    }, 1000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const deleteMessage = async (messageId) => {
    if (!selectedChat) return;
    
    try {
      await api.deleteMessage(selectedChat._id, messageId);
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleMenuToggle = () => {
    setShowChatMenu(!showChatMenu);
  };

  const handleViewProfile = () => {
    if (selectedChat?.otherUser?._id) {
      navigate(`/profile/${selectedChat.otherUser._id}`);
      setShowChatMenu(false);
    }
  };

  const handleBlockUser = async () => {
    if (!selectedChat?.otherUser?._id) return;
    
    try {
      await api.blockUser(selectedChat.otherUser._id);
      // Navigate back to conversations
      setSelectedChat(null);
      setShowChatMenu(false);
      // Reload conversations to reflect the block
      loadConversations();
    } catch (error) {
      console.error('Error blocking user:', error);
    }
  };

  const handleClearChat = async () => {
    if (!selectedChat?._id) return;
    
    if (window.confirm('Are you sure you want to clear all messages in this chat? This action cannot be undone.')) {
      try {
        await api.clearChatHistory(selectedChat._id);
        setMessages([]);
        setShowChatMenu(false);
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
  };

  const handleReportUser = () => {
    if (selectedChat?.otherUser?._id) {
      navigate(`/report-user/${selectedChat.otherUser._id}`);
      setShowChatMenu(false);
    }
  };

  const filteredConversations = conversations.filter(conv => 
    conv.otherUser && 
    (conv.otherUser.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
     (conv.otherUser.firstName && conv.otherUser.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
     (conv.otherUser.lastName && conv.otherUser.lastName.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  const ConversationItem = ({ conversation }) => {
    const isSelected = selectedChat?._id === conversation._id;
    const isUnread = conversation.unreadCount > 0;
    
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => selectChat(conversation)}
        className={`p-4 cursor-pointer rounded-lg transition-all duration-200 ${
          isSelected 
            ? 'bg-primary-100 dark:bg-primary-900 border-primary-500' 
            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
        } ${isUnread ? 'border-l-4 border-primary-500' : ''}`}
      >
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
              {conversation.otherUser?.profileImage ? (
                <img 
                  src={conversation.otherUser.profileImage} 
                  alt={conversation.otherUser.username} 
                  className="w-12 h-12 rounded-full object-cover" 
                />
              ) : (
                <span>{conversation.otherUser?.username?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            {conversation.otherUser?.isOnline && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {conversation.otherUser?.firstName && conversation.otherUser?.lastName
                  ? `${conversation.otherUser.firstName} ${conversation.otherUser.lastName}`
                  : conversation.otherUser?.username
                }
              </h3>
              {conversation.lastMessage && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTime(conversation.lastMessage.timestamp)}
                </span>
              )}
            </div>
            
            {conversation.lastMessage ? (
              <p className={`text-sm truncate ${
                isUnread 
                  ? 'text-gray-900 dark:text-white font-medium' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {conversation.lastMessage.senderId === user.id ? 'You: ' : ''}
                {conversation.lastMessage.content}
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                No messages yet
              </p>
            )}
            
            {isUnread && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                  {conversation.unreadCount} new message{conversation.unreadCount > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const MessageItem = ({ message }) => {
    const isOwnMessage = message.senderId === user.id;
    const sender = message.sender || { username: 'Unknown', profileImage: null };
    
    // Debug logging
    console.log('MessageItem debug:', {
      messageSenderId: message.senderId,
      user_id: user.id,
      isOwnMessage: isOwnMessage,
      messageContent: message.content
    });
    
    if (isOwnMessage) {
      // Your message - right side
      return (
        <div className="flex justify-end mb-4">
          <div className="max-w-xs lg:max-w-md">
            <div className="bg-primary-500 text-white px-4 py-2 rounded-lg rounded-br-none shadow-sm">
              <p className="text-sm">{message.content}</p>
            </div>
            <div className="text-right mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatTime(message.timestamp)}
              </span>
            </div>
          </div>
        </div>
      );
    } else {
      // Friend's message - left side
      return (
        <div className="flex justify-start mb-4">
          <div className="flex items-end space-x-2 max-w-xs lg:max-w-md">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">
              {sender.profileImage ? (
                <img 
                  src={sender.profileImage} 
                  alt={sender.username} 
                  className="w-8 h-8 rounded-full object-cover" 
                />
              ) : (
                <span>{sender.username?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg rounded-bl-none shadow-sm">
                <p className="text-sm">{message.content}</p>
              </div>
              <div className="text-left mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTime(message.timestamp)}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Messages</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Chat with your friends and debate partners
          </p>
        </motion.div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="flex h-[600px]">
            {/* Conversations Sidebar */}
            <div className="w-80 border-r border-gray-200 dark:border-gray-700 flex flex-col">
              {/* Search */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center py-16">
                    <MessageSquare className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {searchQuery ? 'No conversations found' : 'No conversations yet'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {searchQuery 
                        ? 'Try adjusting your search terms'
                        : 'Start chatting with your friends!'
                      }
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => navigate('/friends')}
                        className="btn-primary"
                      >
                        Find Friends
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="p-4 space-y-2">
                    {filteredConversations.map((conversation) => (
                      <ConversationItem key={conversation._id} conversation={conversation} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setSelectedChat(null)}
                        className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </button>
                      
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
                          {selectedChat.otherUser?.profileImage ? (
                            <img 
                              src={selectedChat.otherUser.profileImage} 
                              alt={selectedChat.otherUser.username} 
                              className="w-10 h-10 rounded-full object-cover" 
                            />
                          ) : (
                            <span>{selectedChat.otherUser?.username?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        {selectedChat.otherUser?.isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                        )}
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {selectedChat.otherUser?.firstName && selectedChat.otherUser?.lastName
                            ? `${selectedChat.otherUser.firstName} ${selectedChat.otherUser.lastName}`
                            : selectedChat.otherUser?.username
                          }
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedChat.otherUser?.isOnline ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">

                      <div className="relative" ref={menuRef}>
                        <button 
                          onClick={handleMenuToggle}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        <AnimatePresence>
                          {showChatMenu && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -10 }}
                              transition={{ duration: 0.1 }}
                              className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                            >
                              <div className="py-1">
                                <button
                                  onClick={handleViewProfile}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <User className="w-4 h-4" />
                                  <span>View Profile</span>
                                </button>
                                
                                <button
                                  onClick={handleBlockUser}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <X className="w-4 h-4" />
                                  <span>Block User</span>
                                </button>
                                
                                <button
                                  onClick={handleClearChat}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Clear Chat</span>
                                </button>
                                
                                <button
                                  onClick={handleReportUser}
                                  className="w-full px-4 py-2 text-left text-sm text-orange-600 dark:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                >
                                  <MessageSquare className="w-4 h-4" />
                                  <span>Report User</span>
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-16">
                        <MessageSquare className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          Start a conversation
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Send a message to begin chatting!
                        </p>
                      </div>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <MessageItem key={message._id} message={message} />
                        ))}
                        
                        {/* Typing indicator */}
                        {typingUsers.size > 0 && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center space-x-2 text-gray-500 dark:text-gray-400"
                          >
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                            <span className="text-sm">
                              {Array.from(typingUsers).length === 1 ? 'Someone is typing...' : 'Multiple people are typing...'}
                            </span>
                          </motion.div>
                        )}
                        
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={handleTyping}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          disabled={messageLoading}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        />
                        {isTyping && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                            <div className="flex space-x-1">
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || messageLoading}
                        className="p-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {messageLoading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      Select a conversation
                    </h3>
                    <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                      Choose a friend to start chatting
                    </p>
                    <button
                      onClick={() => navigate('/friends')}
                      className="btn-primary"
                    >
                      Find Friends
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat; 